import jwtDecode from 'jwt-decode'
import { SUPPORTED_LANGUAGES } from '../types/constants'
import { isDebug } from '../utils/misc'
import { VERSION } from '../version'

/**
 * Version safe wrapper for local storage.
 */
export class Safety {

  static unauthenticated_urls = new Set(['', '/app', '/app/login', '/app/register', '/app/forgot-password', '/app/ci'])

  /**
   * Get the storage version number.
   */
  static version(): string {
    return localStorage.getItem('version') || '0'
  }

  /**
   * Refresh storage version number.
   */
  static setVersion() {
    localStorage.setItem('version', VERSION)
  }

  /**
   * Delete all but debug data from storage.
   */
  static clear(): void {
    Safety.clearExcept(['debug', 'language'])
  }

  /**
   * Delete everything but the session (tokens, installer level) and debug data from storage.
   * Used when a new build takes over a session: the cached data was written by the
   * previous build and gets fetched again, the session itself stays valid.
   */
  static clearCache(): void {
    Safety.clearExcept(['debug', 'language', 'token', 'refresh_token', 'installer-level'])
  }

  private static clearExcept(keep: string[]): void {
    for (const [key,] of Object.entries(localStorage)) {
      if (!keep.includes(key)) {
        localStorage.removeItem(key)
      }
    }
  }

  /**
   * Encode storage value.
   */
  static encode(name: string, value: any): string {
    return name === 'token' || name === 'refresh_token' ? value : JSON.stringify(value)
  }

  /**
   * Decode storage value.
   */
  static decode<T>(name: string, value: any): T {
    return name === 'token' || name === 'refresh_token' ? value : JSON.parse(value)
  }

  /**
   * Check if URL needs token.
   * @param url
   */
  static isUnauthenticated(url: string): boolean {
    const path = url.replace(/\/$/, '')
    return Safety.unauthenticated_urls.has(path) || /\/app\/(new-password|verify)\/.+/.test(path)
  }

  /**
   * Fetch storage value.
   *
   * A version change does not drop the tokens here: `adoptSessionForNewBuild()` has
   * already refreshed the cached data (or dropped the session) before the app rendered,
   * and a tab still running the previous build must not wipe the session that a tab
   * reloaded to the new build has just carried over.
   */
  static get<T>(name: string, def: T | null = null): T {

    if (name === 'installer-level' && isDebug('always-installer')) { return true as unknown as T }

    const stored = localStorage.getItem(name)
    if (stored === null) {
      return def as T
    }

    try {
      // Handle special raw values.
      const value = Safety.decode(name, stored)
      // Special limitations.
      if (name === 'language') {
        if (SUPPORTED_LANGUAGES.indexOf(value as string) < 0) {
          return 'en' as unknown as T
        }
      }
      // Verify token expiry time before using.
      if (name === 'token' || name === 'refresh_token') {
        if (value) {
          const exp: number = (jwtDecode(value as string) as { exp: number }).exp
          if (exp * 1000 <= new Date().getTime()) {
            Safety.set(name, undefined)
            return undefined as unknown as T
          }
        }
      }
      return value as T
    } catch (err) {
      console.error(err)
      console.error(`Corrupted localstorage value for ${name}.`)
      return def as T
    }
  }

  /**
   * Check that version matches this client and if not, fetch the new.
   *
   * The session is kept: the reloaded build sees the version change and fetches the
   * data cached in storage again (see `adoptSessionForNewBuild()`).
   */
  static checkVersion(version: string) {
    if (version !== VERSION) {
      document.location = `/?version=${version}`
    }
  }

  /**
   * Store a value.
   */
  static set<T>(name: string, value: T): void {
    if (value === undefined) {
      localStorage.removeItem(name)
    } else {
      localStorage.setItem(name, Safety.encode(name, value))
    }
  }
}
